// Localização: frontend/src/pages/Agendamentos/Agendamentos.js (Versão Otimizada V3.0.2)
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'; 
import { useAuth } from '../../context/AuthContext';
import { useSearch } from '../../context/SearchContext';
import api from '../../api/api';
import moment from 'moment'; 
import Modal from '../../components/UI/Modal';
import AgendamentoForm from './AgendamentoForm'; 
import AgendamentoCard from './AgendamentoCard';
import CalendarView from './CalendarView';

// Componentes Comuns
import LoadingIndicator from '../../components/Common/LoadingIndicator';
import ConfirmacaoModal from '../../components/Common/ConfirmacaoModal';
import Alert from '../../components/UI/Alert'; 

// Filtros
import FloatingCalendarFilter from '../../components/Financeiro/FloatingCalendarFilter';
import AgendamentoStatusFilter from '../../components/Agendamentos/AgendamentoStatusFilter';
import AgendamentoTipoFilter from '../../components/Agendamentos/AgendamentoTipoFilter';
import FilterPaciente from '../../components/Pacientes/FilterPaciente';
import FilterProfissional from '../../components/Agendamentos/FilterProfissional';

// Utilitários
import { parseLocalDateTime } from '../../utils/formatters';
import { getPacienteFotoUrl, getUsuarioFotoUrl } from '../../utils/imageUtils';
import { usePacienteList } from '../../hooks/usePacienteList';
import { useNavigate } from 'react-router-dom';

// Ícones Lucide React
import { 
    Calendar, List, CalendarPlus, XCircle,
    Info, Clock, CalendarCheck, Plus, Tag, User, Users,
    CalendarDays, Sparkles
} from 'lucide-react';

const FALLBACK_AVATAR = '/images/higeia-avatar-shield.png';

// Mapeamento de Status para estilo
const statusStyles = {
    'Pendente': 'bg-yellow-100 text-yellow-800 border-yellow-300',
    'Em Andamento': 'bg-blue-100 text-blue-800 border-blue-300',
    'Concluído': 'bg-green-100 text-green-800 border-green-300',
    'Cancelado': 'bg-red-100 text-red-800 border-red-300',
};

// Modal de Detalhes do Agendamento (Apenas Visualização)
const AgendamentoDetalhesModal = ({ isOpen, onClose, agendamento, pacientes, profissionais, user }) => {
    if (!agendamento) return null;

    const paciente = pacientes.find(p => p.id === agendamento.paciente_id);
    const profissional = profissionais.find(p => p.id === agendamento.usuario_id);
    const nomePaciente = agendamento.paciente_nome || paciente?.nome_completo || 'Não Atribuído';
    const nomeProfissional = agendamento.profissional_nome || profissional?.nome_completo || 'Não Atribuído';
    const statusClass = statusStyles[agendamento.status] || 'bg-gray-100 text-gray-800 border-gray-300';

    // Formatação de datas
    const formatDateTime = (date) => {
        if (!date) return 'Não informado';
        const dateObj = parseLocalDateTime(date);
        if (!dateObj) return 'Não informado';
        return dateObj.toLocaleString('pt-BR', { 
            weekday: 'long', 
            day: '2-digit', 
            month: 'long', 
            year: 'numeric',
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    const formatTime = (date) => {
        if (!date) return 'Não informado';
        const dateObj = parseLocalDateTime(date);
        if (!dateObj) return 'Não informado';
        return dateObj.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };

    const dataInicio = parseLocalDateTime(agendamento.data_hora_inicio);
    const dataFim = agendamento.data_hora_fim ? parseLocalDateTime(agendamento.data_hora_fim) : null;

    // Verificar se o usuário logado é o profissional atribuído (para exibir informação)
    const isProfissionalAtribuido = user?.id === agendamento.usuario_id;

    return (
        <Modal 
            isOpen={isOpen} 
            onClose={onClose} 
            title={
                <div className="flex items-center gap-2">
                    <Info className="text-emerald-600" size={24} />
                    <span>Detalhes do Agendamento</span>
                </div>
            }
            size="xl"
        >
            <div className="space-y-6">
                {/* Informações do Paciente */}
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-5 border border-emerald-200">
                    <div className="flex items-center gap-4">
                        <img 
                            className="h-20 w-20 rounded-full object-cover border-4 border-white shadow-lg" 
                            src={getPacienteFotoUrl(paciente?.fotoURL)} 
                            alt={`Foto de ${nomePaciente}`} 
                            onError={(e) => {
                                e.target.onerror = null; 
                                e.target.src = FALLBACK_AVATAR; 
                            }}
                        />
                        <div className="flex-1">
                            <h3 className="text-2xl font-bold text-gray-900">{nomePaciente}</h3>
                            <p className="text-sm text-gray-600 mt-1">Paciente do Agendamento</p>
                            {paciente?.data_nascimento && (
                                <p className="text-xs text-gray-500 mt-1">
                                    Nascimento: {new Date(paciente.data_nascimento).toLocaleDateString('pt-BR')}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Informações do Profissional */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5 border border-blue-200">
                    <div className="flex items-center gap-4">
                        <img 
                            className="h-20 w-20 rounded-full object-cover border-4 border-white shadow-lg" 
                            src={getUsuarioFotoUrl(profissional?.fotoURL || agendamento.profissional_foto_url)} 
                            alt={`Foto de ${nomeProfissional}`} 
                            onError={(e) => {
                                e.target.onerror = null; 
                                e.target.src = FALLBACK_AVATAR; 
                            }}
                        />
                        <div className="flex-1">
                            <h3 className="text-2xl font-bold text-gray-900">{nomeProfissional}</h3>
                            <p className="text-sm text-gray-600 mt-1">Profissional Responsável</p>
                            {profissional?.role && (
                                <p className="text-xs text-gray-500 mt-1">
                                    Função: {profissional.role}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Informações Principais em Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
                            Tipo de Agendamento
                        </label>
                        <p className="text-lg font-semibold text-gray-900">{agendamento.tipo || 'Não especificado'}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
                            Status Atual
                        </label>
                        <div>
                            <span className={`px-4 py-2 inline-flex text-sm font-semibold rounded-full ${statusClass}`}>
                                {agendamento.status}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Data e Hora de Início */}
                {dataInicio && (
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <div className="flex items-start gap-3">
                            <CalendarCheck className="text-blue-600 mt-0.5 flex-shrink-0" size={22} />
                            <div className="flex-1">
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                                    Data e Hora de Início
                                </label>
                                <p className="text-base font-medium text-gray-900">{formatDateTime(dataInicio)}</p>
                                <p className="text-xs text-gray-600 mt-1">
                                    Horário: {formatTime(dataInicio)}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Data e Hora de Fim */}
                {dataFim && (
                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                        <div className="flex items-start gap-3">
                            <Clock className="text-green-600 mt-0.5 flex-shrink-0" size={22} />
                            <div className="flex-1">
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                                    Data e Hora de Fim
                                </label>
                                <p className="text-base font-medium text-gray-900">{formatDateTime(dataFim)}</p>
                                <p className="text-xs text-gray-600 mt-1">
                                    Horário: {formatTime(dataFim)}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Tipo de Cuidado */}
                {agendamento.tipo_cuidado && (
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
                            Tipo de Cuidado
                        </label>
                        <p className="text-gray-900 leading-relaxed">{agendamento.tipo_cuidado}</p>
                    </div>
                )}

                {/* Descrição */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
                        Descrição
                    </label>
                    <p className="text-gray-900 leading-relaxed whitespace-pre-wrap">
                        {agendamento.descricao || <span className="text-gray-400 italic">Nenhuma descrição informada</span>}
                    </p>
                </div>

                {/* Informação sobre Confirmação */}
                {isProfissionalAtribuido && agendamento.status === 'Pendente' && (
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <div className="flex items-start gap-3">
                            <Info className="text-blue-600 mt-0.5 flex-shrink-0" size={20} />
                            <div className="flex-1">
                                <h4 className="text-sm font-semibold text-blue-900 mb-1">
                                    Confirmação Necessária
                                </h4>
                                <p className="text-xs text-blue-700">
                                    Você foi atribuído a este agendamento. Acesse a página de <strong>Check-in/Check-out</strong> para confirmar sua presença e iniciar o atendimento.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* ID do Agendamento */}
                <div className="pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-400 text-center">
                        ID do Agendamento: {agendamento.id}
                    </p>
                </div>
            </div>
        </Modal>
    );
};




// --------------------------------------------------------------------------------
// Componente Principal AgendamentosPage - ATUALIZADO V3.0.2
// --------------------------------------------------------------------------------
const AgendamentosPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [agendamentos, setAgendamentos] = useState([]);
    const [pacientes, setPacientes] = useState([]);
    const [profissionais, setProfissionais] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alertMessage, setAlertMessage] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentAgendamento, setCurrentAgendamento] = useState(null);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedAgendamento, setSelectedAgendamento] = useState(null);
    const [agendamentoParaDeletar, setAgendamentoParaDeletar] = useState(null); 
    
    // ESTADOS PARA BUSCA
    const { searchTerm, setSearchTerm, setSearchPlaceholder, setOnSearchChange } = useSearch();
    const [viewMode, setViewMode] = useState('list');

    // Estados para botão de opções flutuante
    const [showOpcoesMenu, setShowOpcoesMenu] = useState(false);
    const opcoesMenuRef = useRef(null);
    const opcoesButtonRef = useRef(null);

    // Hook para lista de pacientes (para filtro)
    const { pacientes: pacientesList } = usePacienteList();

    // Chave para localStorage
    const FILTROS_STORAGE_KEY = 'agendamentos_filtros';
    
    // Status padrão pré-selecionados
    const STATUS_PADRAO = ['Pendente', 'Em Andamento'];
    
    // Função para carregar filtros salvos
    const loadSavedFilters = () => {
        try {
            const saved = localStorage.getItem(FILTROS_STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                // Se não houver filtro de status salvo ou estiver vazio, usar os padrões
                const filtroStatusSalvo = parsed.filtroStatus;
                const filtroStatusFinal = (filtroStatusSalvo && Array.isArray(filtroStatusSalvo) && filtroStatusSalvo.length > 0) 
                    ? filtroStatusSalvo 
                    : STATUS_PADRAO;
                
                return {
                    filtroStatus: filtroStatusFinal,
                    filtroTipo: parsed.filtroTipo || [],
                    rangeDatas: parsed.rangeDatas || { startDate: null, endDate: null },
                    pacienteFiltrado: parsed.pacienteFiltrado || null,
                    profissionalFiltrado: parsed.profissionalFiltrado || null,
                    periodoFluxo: parsed.periodoFluxo || 'custom'
                };
            }
        } catch (error) {
            console.error('Erro ao carregar filtros salvos:', error);
        }
        return {
            filtroStatus: STATUS_PADRAO, // Status padrão pré-selecionados
            filtroTipo: [],
            rangeDatas: { startDate: null, endDate: null },
            pacienteFiltrado: null,
            profissionalFiltrado: null,
            periodoFluxo: 'custom'
        };
    };
    
    // Função para salvar filtros
    const saveFilters = (filtros) => {
        try {
            localStorage.setItem(FILTROS_STORAGE_KEY, JSON.stringify(filtros));
        } catch (error) {
            console.error('Erro ao salvar filtros:', error);
        }
    };
    
    // Carregar filtros salvos ao inicializar
    const savedFilters = loadSavedFilters();
    
    // --- Estados de Filtro ---
    const [filtroStatus, setFiltroStatus] = useState(savedFilters.filtroStatus);
    const [filtroTipo, setFiltroTipo] = useState(savedFilters.filtroTipo);
    const [rangeDatas, setRangeDatas] = useState(savedFilters.rangeDatas);
    const [pacienteFiltrado, setPacienteFiltrado] = useState(null);
    const [profissionalFiltrado, setProfissionalFiltrado] = useState(null);
    const [showFilterPaciente, setShowFilterPaciente] = useState(false);
    const [showFilterProfissional, setShowFilterProfissional] = useState(false);
    
    // Estados para filtro de período flutuante
    const [periodoFluxo, setPeriodoFluxo] = useState(savedFilters.periodoFluxo);
    const [showPeriodFilter, setShowPeriodFilter] = useState(false);
    const periodFilterRef = useRef(null);
    const periodButtonRef = useRef(null);
    
    // Estados para filtro de status flutuante
    const [showStatusFilter, setShowStatusFilter] = useState(false);
    const statusFilterRef = useRef(null);
    const statusButtonRef = useRef(null);
    
    // Estados para filtro de tipo flutuante
    const [showTipoFilter, setShowTipoFilter] = useState(false);
    const tipoFilterRef = useRef(null);
    const tipoButtonRef = useRef(null);

    // Salvar filtros sempre que mudarem
    useEffect(() => {
        saveFilters({
            filtroStatus,
            filtroTipo,
            rangeDatas,
            pacienteFiltrado,
            profissionalFiltrado,
            periodoFluxo
        });
    }, [filtroStatus, filtroTipo, rangeDatas, pacienteFiltrado, profissionalFiltrado, periodoFluxo]);

    // Restaurar paciente e profissional filtrado quando a lista carregar
    useEffect(() => {
        if (savedFilters.pacienteFiltrado && pacientesList.length > 0 && !pacienteFiltrado) {
            const pacienteRestaurado = pacientesList.find(p => p.id === savedFilters.pacienteFiltrado?.id);
            if (pacienteRestaurado) {
                setPacienteFiltrado(pacienteRestaurado);
            }
        }
    }, [pacientesList, savedFilters.pacienteFiltrado, pacienteFiltrado]);

    // Handler para mudança de período
    const handlePeriodoChange = useCallback((periodo) => {
        setPeriodoFluxo(periodo);
        
        if (periodo === 'custom') {
            return;
        }

        const today = new Date();
        const start = new Date();
        const end = new Date();

        if (periodo === 'dia') {
            start.setHours(0, 0, 0, 0);
            end.setHours(23, 59, 59, 999);
        } else if (periodo === 'semana') {
            const dayOfWeek = today.getDay();
            start.setDate(today.getDate() - dayOfWeek);
            end.setDate(start.getDate() + 6);
        } else if (periodo === 'mes') {
            start.setDate(1);
            end.setMonth(end.getMonth() + 1);
            end.setDate(0);
        }

        setRangeDatas({
            startDate: start.toISOString().split('T')[0],
            endDate: end.toISOString().split('T')[0]
        });
    }, []);

    // Handler para mudança de range de datas
    const handleDateRangeChange = useCallback((start, end) => {
        setRangeDatas({
            startDate: start || null,
            endDate: end || null
        });
        setPeriodoFluxo('custom');
    }, []);

    // Handler para seleção de paciente
    const handlePacienteSelect = useCallback((paciente) => {
        setPacienteFiltrado(paciente);
    }, []);

    // Handler para resetar filtro de paciente
    const handleResetPacienteFilter = useCallback(() => {
        setPacienteFiltrado(null);
    }, []);

    // Handler para seleção de profissional
    const handleProfissionalSelect = useCallback((profissional) => {
        setProfissionalFiltrado(profissional);
    }, []);

    // Handler para resetar filtro de profissional
    const handleResetProfissionalFilter = useCallback(() => {
        setProfissionalFiltrado(null);
    }, []);

    // Permissões
    const canManage = ['admin', 'proprietario', 'gerente'].includes(user?.role?.toLowerCase());

    // --- LÓGICA DE CARREGAMENTO ---
    const fetchAgendamentos = useCallback(async () => {
        setLoading(true);
        setAlertMessage(null);
        try {
            const response = await api.get('/agendamentos'); 
            setAgendamentos(response.data);
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Falha ao carregar agendamentos. Verifique o servidor.';
            setAlertMessage({ type: 'error', message: errorMessage });
            console.error('Erro no GET /agendamentos:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Buscar pacientes e profissionais
    const fetchPacientes = useCallback(async () => {
        try {
            const response = await api.get('/pacientes');
            setPacientes(response.data || []);
        } catch (err) {
            console.error('Erro ao carregar pacientes:', err);
        }
    }, []);

    const fetchProfissionais = useCallback(async () => {
        try {
            const response = await api.get('/usuarios');
            // Filtrar apenas profissionais (roles que podem ter agendamentos)
            const profs = (response.data || []).filter(u => 
                ['enfermeiro', 'medico', 'fisioterapeuta', 'cuidador', 'gerente', 'admin', 'proprietario'].includes(
                    u.role?.toLowerCase()
                )
            );
            setProfissionais(profs);
        } catch (err) {
            console.error('Erro ao carregar profissionais:', err);
        }
    }, []);

    useEffect(() => {
        if (user) {
            fetchAgendamentos();
            fetchPacientes();
            fetchProfissionais();
        }
    }, [user, fetchAgendamentos, fetchPacientes, fetchProfissionais]);

    // Configurar o contexto de busca para esta página
    useEffect(() => {
        setSearchPlaceholder('Buscar por Paciente, Profissional, Cuidado...');
        setOnSearchChange((value) => {
            setSearchTerm(value);
        });
        // Cleanup ao sair da página
        return () => {
            setOnSearchChange(null);
        };
    }, [setSearchPlaceholder, setOnSearchChange, setSearchTerm]);

    // Fechar menu de opções ao clicar fora
    useEffect(() => {
        if (!showOpcoesMenu) return;

        const handleClickOutside = (event) => {
            if (
                opcoesMenuRef.current &&
                opcoesButtonRef.current &&
                !opcoesMenuRef.current.contains(event.target) &&
                !opcoesButtonRef.current.contains(event.target)
            ) {
                setShowOpcoesMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showOpcoesMenu]);

    // Fechar menu ao pressionar Escape
    useEffect(() => {
        if (!showOpcoesMenu) return;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setShowOpcoesMenu(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [showOpcoesMenu]);

    // Fechar dropdown de período ao clicar fora
    useEffect(() => {
        if (!showPeriodFilter) return;

        const handleClickOutside = (event) => {
            if (
                periodFilterRef.current &&
                periodButtonRef.current &&
                !periodFilterRef.current.contains(event.target) &&
                !periodButtonRef.current.contains(event.target)
            ) {
                setShowPeriodFilter(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showPeriodFilter]);

    // Fechar dropdown de período ao pressionar Escape
    useEffect(() => {
        if (!showPeriodFilter) return;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setShowPeriodFilter(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [showPeriodFilter]);

    // Fechar dropdown de status ao clicar fora
    useEffect(() => {
        if (!showStatusFilter) return;

        const handleClickOutside = (event) => {
            if (
                statusFilterRef.current &&
                statusButtonRef.current &&
                !statusFilterRef.current.contains(event.target) &&
                !statusButtonRef.current.contains(event.target)
            ) {
                setShowStatusFilter(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showStatusFilter]);

    // Fechar dropdown de status ao pressionar Escape
    useEffect(() => {
        if (!showStatusFilter) return;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setShowStatusFilter(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [showStatusFilter]);

    // Fechar dropdown de tipo ao clicar fora
    useEffect(() => {
        if (!showTipoFilter) return;

        const handleClickOutside = (event) => {
            if (
                tipoFilterRef.current &&
                tipoButtonRef.current &&
                !tipoFilterRef.current.contains(event.target) &&
                !tipoButtonRef.current.contains(event.target)
            ) {
                setShowTipoFilter(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showTipoFilter]);

    // Fechar dropdown de tipo ao pressionar Escape
    useEffect(() => {
        if (!showTipoFilter) return;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setShowTipoFilter(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [showTipoFilter]);

    // --- FUNÇÕES DE CRUD (Mantidas Inalteradas) ---
    // ... (handleSaveAgendamento, handleEditClick, handleDeleteAgendamento, handleUpdateStatus)

    // A função handleUpdateStatus permanece a mesma:
    const handleUpdateStatus = async (id, newStatus) => {
        if (!canManage) {
            setAlertMessage({ type: 'warning', message: 'Você não tem permissão para alterar o status.' });
            return;
        }

        const actionText = newStatus === 'Em Andamento' ? 'iniciar' : (newStatus === 'Concluído' ? 'concluir' : 'alterar o status para ' + newStatus);
        if (!window.confirm(`Tem certeza que deseja ${actionText} este agendamento?`)) {
            return;
        }

        setLoading(true);
        setAlertMessage(null);
        try {
            await api.patch(`/agendamentos/${id}/status`, { status: newStatus }); 
            setAlertMessage({ type: 'success', message: `Status do agendamento alterado para '${newStatus}' com sucesso!` });
            fetchAgendamentos();
        } catch (err) {
            const errorMessage = err.response?.data?.message || `Erro ao ${actionText} o agendamento.`;
            console.error(`Erro ao atualizar status para ${newStatus}:`, err);
            setAlertMessage({ type: 'error', message: errorMessage });
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = (agendamento) => {
        if (!canManage) {
            setAlertMessage({ type: 'warning', message: 'Você não tem permissão para editar agendamentos.' });
            return;
        }

        const formattedAgendamento = {
            ...agendamento,
            data_hora_inicio: agendamento.data_hora_inicio ? agendamento.data_hora_inicio.substring(0, 16) : '',
            data_hora_fim: agendamento.data_hora_fim ? agendamento.data_hora_fim.substring(0, 16) : '',
        };
        setCurrentAgendamento(formattedAgendamento);
        setIsModalOpen(true);
    };

    const handleDeleteAgendamento = (id) => {
        if (!canManage) {
            setAlertMessage({ type: 'warning', message: 'Você não tem permissão para excluir agendamentos.' });
            return;
        }
        // Encontrar o agendamento para exibir informações no modal
        const agendamento = agendamentos.find(a => a.id === id);
        setAgendamentoParaDeletar(agendamento || { id });
    };

    const handleConfirmarDelete = async () => {
        if (!agendamentoParaDeletar) return;
        
        const id = agendamentoParaDeletar.id;
        setLoading(true);
        setAlertMessage(null);
        try {
            await api.delete(`/agendamentos/${id}`);
            setAlertMessage({ type: 'success', message: 'Agendamento excluído com sucesso!' });
            setAgendamentoParaDeletar(null);
            fetchAgendamentos();
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Erro ao excluir agendamento.';
            console.error('Erro ao excluir agendamento:', err);
            setAlertMessage({ type: 'error', message: errorMessage });
        } finally {
            setLoading(false);
        }
    };
    
    const handleSaveAgendamento = async (formData) => {
        setLoading(true);
        setAlertMessage(null);
        try {
            console.log('[Agendamentos] Salvando agendamento:', {
                isEdit: !!currentAgendamento,
                formData: {
                    ...formData,
                    usuario_id: formData.usuario_id,
                    user_id: user?.id
                }
            });
            if (currentAgendamento) {
                await api.put(`/agendamentos/${currentAgendamento.id}`, formData);
                setAlertMessage({ type: 'success', message: 'Agendamento atualizado com sucesso!' });
            } else {
                await api.post('/agendamentos', formData);
                setAlertMessage({ type: 'success', message: 'Agendamento criado com sucesso!' });
            }
            handleCloseModal(); 
            fetchAgendamentos(); 
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Erro de rede ou servidor ao salvar agendamento.';
            console.error('Erro ao salvar agendamento:', err);
            setAlertMessage({ type: 'error', message: errorMessage });
        } finally {
            setLoading(false);
        }
    };

    // Handlers para o calendário
    const handleCalendarSelectSlot = async (slotData) => {
        // Criar agendamento a partir do slot selecionado
        const formData = {
            paciente_id: slotData.paciente_id,
            usuario_id: slotData.usuario_id || user.id,
            tipo: slotData.tipo || 'Visita',
            descricao: slotData.descricao || '',
            data_hora_inicio: moment(slotData.start).format('YYYY-MM-DDTHH:mm'),
            data_hora_fim: slotData.end ? moment(slotData.end).format('YYYY-MM-DDTHH:mm') : null,
            tipo_cuidado: slotData.tipo_cuidado || '',
            status: 'Pendente',
        };
        await handleSaveAgendamento(formData);
    };

    const handleCalendarEventDrop = async (eventData) => {
        // Mover agendamento
        const formData = {
            paciente_id: eventData.paciente_id,
            usuario_id: eventData.usuario_id,
            tipo: eventData.tipo,
            descricao: eventData.descricao,
            data_hora_inicio: moment(eventData.start).format('YYYY-MM-DDTHH:mm'),
            data_hora_fim: eventData.end ? moment(eventData.end).format('YYYY-MM-DDTHH:mm') : null,
            tipo_cuidado: eventData.tipo_cuidado,
            status: eventData.status,
        };
        setCurrentAgendamento({ id: eventData.id });
        await handleSaveAgendamento(formData);
    };

    const handleCalendarEventResize = async (eventData) => {
        // Redimensionar agendamento
        await handleCalendarEventDrop(eventData);
    };

    const handleCalendarEventClick = (agendamento) => {
        // Abrir modal de edição ao clicar no evento
        const formattedAgendamento = {
            ...agendamento,
            data_hora_inicio: agendamento.data_hora_inicio ? agendamento.data_hora_inicio.substring(0, 16) : '',
            data_hora_fim: agendamento.data_hora_fim ? agendamento.data_hora_fim.substring(0, 16) : '',
        };
        setCurrentAgendamento(formattedAgendamento);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setCurrentAgendamento(null);
        setAlertMessage(null); 
    };

    // Handler para abrir modal de detalhes
    const handleViewDetails = (agendamento) => {
        setSelectedAgendamento(agendamento);
        setIsDetailsModalOpen(true);
    };

    // Handler para fechar modal de detalhes
    const handleCloseDetailsModal = () => {
        setIsDetailsModalOpen(false);
        setSelectedAgendamento(null);
    };


    // --- LÓGICA DE FILTRO OTIMIZADA COM useMemo ---
    
    const filteredAgendamentos = useMemo(() => {
        return agendamentos.filter(agendamento => {
            // Filtro por Busca (Search) - com verificação de null/undefined
            const searchLower = (searchTerm || '').toLowerCase().trim();
            const searchMatch = !searchLower || (
                (agendamento.paciente_nome?.toLowerCase() || '').includes(searchLower) ||
                (agendamento.profissional_nome?.toLowerCase() || '').includes(searchLower) ||
                (agendamento.tipo_cuidado?.toLowerCase() || '').includes(searchLower)
            );

            // Filtro por Status
            const statusArray = Array.isArray(filtroStatus) ? filtroStatus : (filtroStatus ? [filtroStatus] : []);
            const correspondeStatus = statusArray.length === 0 || statusArray.includes(agendamento.status);

            // Filtro por Tipo de Agendamento
            const tipoArray = Array.isArray(filtroTipo) ? filtroTipo : (filtroTipo ? [filtroTipo] : []);
            const correspondeTipo = tipoArray.length === 0 || tipoArray.includes(agendamento.tipo);

            // Filtro por Paciente
            const correspondePaciente = !pacienteFiltrado || agendamento.paciente_id === pacienteFiltrado.id;

            // Filtro por Profissional
            const correspondeProfissional = !profissionalFiltrado || agendamento.usuario_id === profissionalFiltrado.id;

            // Filtro de datas
            const startDate = rangeDatas.startDate && rangeDatas.startDate !== '' ? new Date(rangeDatas.startDate) : null;
            const endDate = rangeDatas.endDate && rangeDatas.endDate !== '' ? new Date(rangeDatas.endDate) : null;
            
            let correspondeDataInicio = true;
            let correspondeDataFim = true;
            
            if (agendamento.data_hora_inicio) {
                const dataInicio = parseLocalDateTime(agendamento.data_hora_inicio);
                if (dataInicio) {
                    const dataAgendada = new Date(dataInicio.getFullYear(), dataInicio.getMonth(), dataInicio.getDate());
                    
                    if (startDate) {
                        const dataInicioFiltro = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
                        correspondeDataInicio = dataAgendada >= dataInicioFiltro;
                    }
                    
                    if (endDate) {
                        const dataFimFiltro = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
                        dataFimFiltro.setDate(dataFimFiltro.getDate() + 1);
                        correspondeDataFim = dataAgendada < dataFimFiltro;
                    }
                }
            }

            return searchMatch && correspondeStatus && correspondeTipo && correspondePaciente && correspondeProfissional && correspondeDataInicio && correspondeDataFim;
        });
    }, [agendamentos, searchTerm, filtroStatus, filtroTipo, pacienteFiltrado, profissionalFiltrado, rangeDatas]);

    // --- Renderização ---
    
    if (loading && agendamentos.length === 0) {
        return <LoadingIndicator message="Carregando Agendamentos..." />;
    }

    return (
        <div className="p-4 sm:p-6 lg:p-8 min-h-screen"> 
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                        <div className="flex-1">
                            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
                                <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                                    Agendamentos
                                </span>
                            </h1>
                            <p className="text-sm text-gray-500 font-medium">
                                Gerencie e acompanhe todos os agendamentos e compromissos
                            </p>
                        </div>
                    </div>
                </div>

                {/* Botão Flutuante à Direita - Opções */}
                <div className="fixed top-20 right-4 sm:right-6 lg:right-8 z-30">
                    <div ref={opcoesButtonRef}>
                        <button
                            onClick={() => setShowOpcoesMenu(!showOpcoesMenu)}
                            className={`
                                flex items-center justify-center gap-2
                                px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg
                                transition-all duration-200 font-medium 
                                text-xs sm:text-sm
                                border shadow-lg hover:shadow-xl
                                backdrop-blur-sm
                                ${showOpcoesMenu 
                                    ? 'bg-gray-100 text-gray-700 border-gray-300 border-[0.5px] shadow-xl font-semibold' 
                                    : 'bg-white/80 text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                }
                            `}
                            title={showOpcoesMenu ? 'Ocultar menu de opções' : 'Mostrar menu de opções'}
                        >
                            <Plus className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ${showOpcoesMenu ? 'scale-110' : ''} transition-transform`} />
                            <span className="hidden sm:inline whitespace-nowrap">Opções</span>
                        </button>
                    </div>
                </div>

                {/* Menu Dropdown de Opções */}
                {showOpcoesMenu && (
                    <div ref={opcoesMenuRef} className="fixed top-32 sm:top-36 right-4 sm:right-6 lg:right-8 z-40 w-[calc(100%-2rem)] sm:w-auto sm:min-w-[200px]">
                        <div className="bg-white rounded-xl shadow-2xl border border-gray-200 py-2">
                            {canManage && (
                                <button
                                    onClick={() => {
                                        setCurrentAgendamento(null);
                                        setIsModalOpen(true);
                                        setAlertMessage(null);
                                        setShowOpcoesMenu(false);
                                    }}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-emerald-50 transition-colors text-left"
                                >
                                    <CalendarPlus className="h-5 w-5 text-emerald-600" />
                                    <span className="text-sm font-medium text-gray-700">Novo Agendamento</span>
                                </button>
                            )}
                            {/* Botão para Escalas de Plantão */}
                            {(user?.role === 'admin' || user?.role === 'gerente' || user?.role === 'proprietario') && (
                                <>
                                    <div className="border-t border-gray-200 my-1"></div>
                                    <button
                                        onClick={() => {
                                            navigate('/escalas');
                                            setShowOpcoesMenu(false);
                                        }}
                                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-purple-50 transition-colors text-left"
                                    >
                                        <div className="relative">
                                            <CalendarDays className="h-5 w-5 text-purple-600" />
                                            <Sparkles className="h-3 w-3 text-purple-400 absolute -top-1 -right-1" />
                                        </div>
                                        <span className="text-sm font-medium text-gray-700">Escalas de Plantão</span>
                                    </button>
                                </>
                            )}
                            <div className="border-t border-gray-200 my-1"></div>
                            <button
                                onClick={() => {
                                    setViewMode('list');
                                    setShowOpcoesMenu(false);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 transition-colors text-left"
                            >
                                <List className="h-5 w-5 text-blue-600" />
                                <span className="text-sm font-medium text-gray-700">Visualização em Lista</span>
                            </button>
                            <button
                                onClick={() => {
                                    setViewMode('calendar');
                                    setShowOpcoesMenu(false);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-purple-50 transition-colors text-left"
                            >
                                <Calendar className="h-5 w-5 text-purple-600" />
                                <span className="text-sm font-medium text-gray-700">Visualização em Calendário</span>
                            </button>
                            <div className="border-t border-gray-200 my-1"></div>
                            <button
                                onClick={() => {
                                    setShowPeriodFilter(!showPeriodFilter);
                                    setShowOpcoesMenu(false);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 transition-colors text-left"
                            >
                                <Calendar className="h-5 w-5 text-blue-600" />
                                <span className="text-sm font-medium text-gray-700">Período</span>
                            </button>
                            <button
                                ref={statusButtonRef}
                                onClick={() => {
                                    setShowStatusFilter(!showStatusFilter);
                                    setShowTipoFilter(false);
                                    setShowOpcoesMenu(false);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-purple-50 transition-colors text-left"
                            >
                                <Tag className="h-5 w-5 text-purple-600" />
                                <span className="text-sm font-medium text-gray-700">Status</span>
                            </button>
                            <button
                                ref={tipoButtonRef}
                                onClick={() => {
                                    setShowTipoFilter(!showTipoFilter);
                                    setShowStatusFilter(false);
                                    setShowOpcoesMenu(false);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-teal-50 transition-colors text-left"
                            >
                                <Tag className="h-5 w-5 text-teal-600" />
                                <span className="text-sm font-medium text-gray-700">Tipo de Agendamento</span>
                            </button>
                            <div className="border-t border-gray-200 my-1"></div>
                            <button
                                onClick={() => {
                                    setShowFilterPaciente(!showFilterPaciente);
                                    setShowOpcoesMenu(false);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-orange-50 transition-colors text-left"
                            >
                                <User className="h-5 w-5 text-orange-600" />
                                <span className="text-sm font-medium text-gray-700">
                                    {showFilterPaciente ? 'Ocultar Pacientes' : 'Mostrar Pacientes'}
                                </span>
                            </button>
                            <button
                                onClick={() => {
                                    setShowFilterProfissional(!showFilterProfissional);
                                    setShowOpcoesMenu(false);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-indigo-50 transition-colors text-left"
                            >
                                <Users className="h-5 w-5 text-indigo-600" />
                                <span className="text-sm font-medium text-gray-700">
                                    {showFilterProfissional ? 'Ocultar Profissionais' : 'Mostrar Profissionais'}
                                </span>
                            </button>
                        </div>
                    </div>
                )}

                {/* FilterPaciente - Posicionado no topo centralizado (oculto inicialmente) */}
                {showFilterPaciente && (
                    <FilterPaciente 
                        onPacienteSelect={handlePacienteSelect}
                        onClearSelection={handleResetPacienteFilter}
                        selectedPaciente={pacienteFiltrado}
                        position="top-center"
                    />
                )}

                {/* FilterProfissional - Posicionado abaixo do FilterPaciente quando ambos estão visíveis */}
                {showFilterProfissional && (
                    <FilterProfissional 
                        onProfissionalSelect={handleProfissionalSelect}
                        onClearSelection={handleResetProfissionalFilter}
                        selectedProfissional={profissionalFiltrado}
                        position="top-center"
                    />
                )}

                {/* Filtro de Status Flutuante - Dropdown */}
                {showStatusFilter && (
                    <div ref={statusFilterRef} className="fixed top-56 sm:top-60 right-4 sm:right-6 lg:right-8 z-40 w-[calc(100%-2rem)] sm:w-auto sm:min-w-[200px]">
                        <AgendamentoStatusFilter
                            status={filtroStatus}
                            onStatusChange={(newStatus) => {
                                setFiltroStatus(newStatus);
                            }}
                            onClose={() => setShowStatusFilter(false)}
                        />
                    </div>
                )}

                {/* Filtro de Tipo de Agendamento Flutuante - Dropdown */}
                {showTipoFilter && (
                    <div ref={tipoFilterRef} className="fixed top-56 sm:top-60 right-4 sm:right-6 lg:right-8 z-40 w-[calc(100%-2rem)] sm:w-auto sm:min-w-[220px]">
                        <AgendamentoTipoFilter
                            tipos={filtroTipo}
                            onTiposChange={(newTipos) => {
                                setFiltroTipo(newTipos);
                            }}
                            onClose={() => setShowTipoFilter(false)}
                        />
                    </div>
                )}

                {/* Filtro de Calendário Flutuante - Dropdown */}
                {showPeriodFilter && (
                    <div ref={periodFilterRef} className="fixed top-32 sm:top-36 right-4 sm:right-6 lg:right-8 z-40 w-[calc(100%-2rem)] sm:w-auto sm:max-w-md">
                        <FloatingCalendarFilter
                            periodo={periodoFluxo}
                            onPeriodoChange={handlePeriodoChange}
                            startDate={rangeDatas.startDate || ''}
                            endDate={rangeDatas.endDate || ''}
                            onDateRangeChange={handleDateRangeChange}
                            noNegativeMargin={true}
                            customZIndex="z-40"
                            showOnlyContent={true}
                            onClose={() => setShowPeriodFilter(false)}
                        />
                    </div>
                )}

                {/* Componente de Alerta (Sucesso/Erro/Aviso) */}
                {alertMessage && (
                    <div className="mb-4">
                        <Alert 
                            type={alertMessage.type} 
                            message={alertMessage.message} 
                            onClose={() => setAlertMessage(null)} 
                        />
                    </div>
                )}
                
                {/* Indicador de Loading para atualizações (não inicial) */}
                {loading && agendamentos.length > 0 && <LoadingIndicator message="Atualizando lista..." small />}

                {/* Conteúdo Principal: Lista ou Calendário */}
                {viewMode === 'list' ? (
                    /* Grid de Cards - Responsivo: 2 colunas no mobile (2x2), 3 no tablet, 4 no desktop */
                    filteredAgendamentos.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
                            {filteredAgendamentos.map(agendamento => (
                                <AgendamentoCard
                                    key={agendamento.id} 
                                    agendamento={agendamento} 
                                    onEdit={handleEditClick}
                                    onDelete={handleDeleteAgendamento}
                                    onUpdateStatus={handleUpdateStatus}
                                    onViewDetails={handleViewDetails}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 bg-white rounded-xl shadow-lg">
                            <XCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                            <p className="text-gray-500 text-lg">
                                Nenhum agendamento encontrado que corresponda aos filtros atuais.
                            </p>
                        </div>
                    )
                ) : (
                    /* Visualização em Calendário */
                    <CalendarView 
                        agendamentos={filteredAgendamentos}
                        pacientes={pacientes}
                        profissionais={profissionais}
                        onSelectSlot={handleCalendarSelectSlot}
                        onEventDrop={handleCalendarEventDrop}
                        onEventClick={handleCalendarEventClick}
                        canManage={canManage}
                    /> 
                )}
                
                {/* Modal de Criação/Edição */}
                <Modal 
                    isOpen={isModalOpen} 
                    onClose={handleCloseModal} 
                    title={currentAgendamento ? "Editar Agendamento" : "Criar Novo Agendamento"}
                    size="xl"
                >
                    <AgendamentoForm 
                        onSubmit={handleSaveAgendamento} 
                        initialData={currentAgendamento || {}}
                        onCancel={handleCloseModal}
                    />
                </Modal>

                {/* Modal de Detalhes do Agendamento (Apenas Visualização) */}
                <AgendamentoDetalhesModal
                    isOpen={isDetailsModalOpen}
                    onClose={handleCloseDetailsModal}
                    agendamento={selectedAgendamento}
                    pacientes={pacientes}
                    profissionais={profissionais}
                    user={user}
                />

                {/* Modal de Confirmação de Exclusão */}
                <ConfirmacaoModal
                    isOpen={!!agendamentoParaDeletar}
                    onClose={() => setAgendamentoParaDeletar(null)}
                    onConfirm={handleConfirmarDelete}
                    titulo="Excluir Agendamento"
                    mensagem={
                        agendamentoParaDeletar?.paciente_nome || agendamentoParaDeletar?.tipo_cuidado
                            ? `Tem certeza que deseja excluir este agendamento?<br/><br/><strong>Paciente:</strong> ${agendamentoParaDeletar.paciente_nome || 'Não informado'}<br/><strong>Tipo:</strong> ${agendamentoParaDeletar.tipo || 'Não informado'}<br/><strong>Cuidado:</strong> ${agendamentoParaDeletar.tipo_cuidado || 'Não informado'}<br/><br/>Esta ação é <strong>irreversível</strong> e não pode ser desfeita.`
                            : "Tem certeza que deseja excluir este agendamento? Esta ação é <strong>irreversível</strong> e não pode ser desfeita."
                    }
                    confirmText="Excluir Agendamento"
                    cancelText="Cancelar"
                />
            </div>
        </div>
    );
};

export default AgendamentosPage;